import {ApiContractBlueprintCalculations, Localized} from './api';
import {ApiContractBlueprintGroupWithPerkbase} from './purchase';
import {decrypt, encrypt, hash} from './crypto';
import {HtmlHelper} from './htmlHelper';
import {ContractBlueprintForcedModification} from '../../types/api/getContractBlueprints';

export enum SessionInfo {
  purchase_maintainBpVariant = 'purchase_maintainBpVariant',
  purchase_blueprintVariant = 'purchase_blueprintVariant',
  purchase_availableProducts = 'purchase_availableProducts',
  purchase_productInfo = 'purchase_productInfo',
  // we created a v2 here, because of the cache. There may be users
  // that have a v1 in the cache and it would be incompatible.
  purchase_productInfoV2 = 'purchase_productInfo_v2',
  purchase_incompleteInvoiceInfo = 'purchase_incompleteInvoiceInfo',
  purchase_successfulPaymentInfo = 'purchase_successfulPaymentInfo',
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SessionObject extends Record<string, unknown> {}

export interface AvailableProducts extends SessionObject {
  blueprintGroups: ApiContractBlueprintGroupWithPerkbase[];
  userId: string;
  blueprintVariant: string;
  lastUpdate: number;
}

export interface ProductInfoV2 extends SessionObject {
  blueprintGroupId: string;
  bpGroupName: string;
  blueprintId: string;
  blueprintName?: string;
  price: ContractBlueprintPrice;
  forcedModification?: ContractBlueprintForcedModification;
  perk: {
    percentSaved?: number;
    priceToCompareTo?: number;
  };
}

export interface ContractBlueprintPrice {
  currency: string;
  firstPeriod: PricePeriodInfo | null;
  period: PricePeriodInfo;
  displayPeriod: PricePeriodInfo & {
    periodSingleIntervalPrice: number;
  };
  discount: {
    normalPrice: {
      periodPrice: number | null;
      displayPeriodPrice: number | null;
      displayPeriodSingleIntervalPrice: number | null;
    };
  } | null;
}

export interface PricePeriodInfo {
  periodInterval: DisplayInterval;
  periodIntervalCount: number;
  periodPrice: number;
}

export type DisplayInterval = 'yearly' | 'monthly' | 'weekly';

export interface ProductInfo extends SessionObject {
  blueprintGroupId: string;
  blueprintId: string;
  blueprintName?: Localized<string>;
  bpGroupName: Localized<string>;
  periodMonths: number;
  periodWeeks: number;
  periodPrice: number;
  currency: string;

  calculations: ApiContractBlueprintCalculations;

  displayCalculations: {
    perkPercent?: number;
  };

  firstPeriod?: {
    weeks: number;
    months: number;
    periodPrice: number;
  };
}

export interface IncompleteInvoiceInfo extends SessionObject {
  invoiceId: string;
  expires: Date;
}

export interface SuccessfulPaymentInfo extends SessionObject {
  contractId: string;
  amount?: {
    currency: string;
    value: number;
  };
  blueprintGroup?: string;
  blueprint?: string;
  startsAt?: string;
  adyenTestEnvironment?: boolean;
  operation?: string;
}

export interface ISessionService {
  set(key: SessionInfo | string, value: string): void;
  get(key: SessionInfo | string): string | null;
  getBool(key: SessionInfo): boolean | null;
  getBoolFallback(key: SessionInfo, fallback: boolean): boolean;
  setBool(key: SessionInfo, value: boolean): void;
  getObject<T extends SessionObject>(key: SessionInfo | string): T | null;
  setObject<T extends SessionObject>(key: SessionInfo | string, value: T): void;
  delete(key: SessionInfo): void;
}

export class Session implements ISessionService {
  private static _instance: Session | null = null;

  private constructor() {
    return;
  }

  public static get instance(): Session {
    if (!Session._instance) Session._instance = new Session();
    return Session._instance;
  }

  public set(key: SessionInfo | string, value: string): void {
    const keyHa = hash(key);
    const valEn = encrypt(value);

    HtmlHelper.instance.window.sessionStorage.setItem(keyHa, valEn);
  }

  public get(key: SessionInfo | string): string | null {
    try {
      const keyHa = hash(key);

      const valEn = HtmlHelper.instance.window.sessionStorage.getItem(keyHa);
      if (!valEn) return null;
      const decrypted = decrypt(valEn);
      return decrypted;
    } catch (err) {
      return null;
    }
  }

  public getBool(key: SessionInfo): boolean | null {
    const raw = this.get(key);
    if (!raw) return null;
    return raw === 'true';
  }

  public getBoolFallback(key: SessionInfo, fallback: boolean): boolean {
    const ret = this.getBool(key);
    return ret === null ? fallback : ret;
  }

  public setBool(key: SessionInfo, value: boolean): void {
    this.set(key, value.toString());
  }

  public getObject<T extends SessionObject>(
    key: SessionInfo | string,
  ): T | null {
    const raw = this.get(key);
    if (!raw) return null;
    return JSON.parse(raw);
  }

  public setObject<T extends SessionObject>(
    key: SessionInfo | string,
    value: T,
  ): void {
    this.set(key, JSON.stringify(value));
  }

  public delete(key: SessionInfo): void {
    const keyHa = hash(key);
    HtmlHelper.instance.window.sessionStorage.removeItem(keyHa);
  }
}
